import { supabase } from '../supabase/client';

export interface Organization {
  id: string;
  name: string;
  sortOrder: number;
}

export interface CourseCategory {
  id: string;
  name: string;
  code: string;
  orgId: string;
  sortOrder: number;
}

export interface Level {
  id: string;
  name: string;
  catId: string;
  sortOrder: number;
  priceMonth: any;
  priceQuarter: any;
  priceHalfYear: any;
  priceYear: any;
  singleChoiceCount: number;
  multiChoiceCount: number;
  judgeCount: number;
  programmingCount: number;
}

export interface InstitutionPrice {
  id: string;
  levelId: string;
  level_text?: string;
  peopleCount: number;
  priceMonth: any;
  priceQuarter: any;
  priceHalfYear: any;
  priceYear: any;
  sortOrder: number;
}

class CategoryStore {
  private organizations: Organization[] = [];
  private categories: CourseCategory[] = [];
  private levels: Level[] = [];
  private institutionPrices: InstitutionPrice[] = [];
  private listeners: (() => void)[] = [];
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.loadAll().then(() => {
      this.initialized = true;
    });
    return this.initPromise;
  }

  async forceReload(): Promise<void> {
    this.initialized = false;
    this.initPromise = null;
    await this.init();
  }

  async loadAll(): Promise<void> {
    await Promise.all([
      this.loadOrganizations(),
      this.loadCategories(),
      this.loadLevels(),
      this.loadInstitutionPrices()
    ]);
  }

  async reloadAll(): Promise<void> {
    this.initialized = false;
    await this.loadAll();
    this.initialized = true;
    this.notify();
  }

  async loadOrganizations(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('is_enabled', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('加载考评体系失败:', error);
        return;
      }

      this.organizations = (data || []).map(o => ({
        id: o.id.toString(),
        name: o.name,
        sortOrder: o.sort_order || 0
      }));
      this.notify();
    } catch (err) {
      console.error('加载考评体系异常:', err);
    }
  }

  async loadCategories(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('course_categories')
        .select('*')
        .eq('is_enabled', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('加载课程分类失败:', error);
        return;
      }

      this.categories = (data || []).map(c => ({
        id: c.id.toString(),
        name: c.name,
        code: c.code || '',
        orgId: c.org_id?.toString() || '',
        sortOrder: c.sort_order || 0
      }));
      this.notify();
    } catch (err) {
      console.error('加载课程分类异常:', err);
    }
  }

  async loadLevels(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('exam_levels')
        .select('*')
        .eq('is_enabled', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('加载级别失败:', error);
        return;
      }

      this.levels = (data || []).map(l => ({
        id: l.id.toString(),
        name: l.level_name,
        catId: l.cat_id?.toString() || '',
        sortOrder: l.sort_order || 0,
        priceMonth: l.price_month,
        priceQuarter: l.price_quarter,
        priceHalfYear: l.price_half_year,
        priceYear: l.price_year,
        singleChoiceCount: l.single_choice_count || 0,
        multiChoiceCount: l.multi_choice_count || 0,
        judgeCount: l.judge_count || 0,
        programmingCount: l.programming_count || 0
      }));
      this.notify();
    } catch (err) {
      console.error('加载级别异常:', err);
    }
  }

  async loadInstitutionPrices(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('institution_prices')
        .select('*')
        .eq('is_enabled', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('加载机构定价失败:', error);
        return;
      }

      this.institutionPrices = (data || []).map(p => ({
        id: p.id.toString(),
        levelId: p.level_id.toString(),
        peopleCount: p.people_count,
        priceMonth: p.price_month,
        priceQuarter: p.price_quarter,
        priceHalfYear: p.price_half_year,
        priceYear: p.price_year,
        sortOrder: p.sort_order || 0
      }));
      this.notify();
    } catch (err) {
      console.error('加载机构定价异常:', err);
    }
  }

  getOrganizations(): Organization[] {
    return [...this.organizations].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getCategories(): CourseCategory[] {
    return [...this.categories].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getLevels(): Level[] {
    return [...this.levels].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getInstitutionPrices(): InstitutionPrice[] {
    return [...this.institutionPrices].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getInstitutionPricesByLevel(levelId: string): InstitutionPrice[] {
    return this.getInstitutionPrices().filter(p => p.levelId === levelId);
  }

  getCategoriesByOrg(orgId: string): CourseCategory[] {
    return this.getCategories().filter(c => c.orgId === orgId);
  }

  getLevelsByCategory(catId: string): Level[] {
    return this.getLevels().filter(l => l.catId === catId);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取课程分类 code→name 的动态映射表（替代硬编码 CATEGORY_TYPE_MAP）
   * 数据来源于 course_categories 表，与【类目与定价】栏目实时同步
   */
  getCategoryTypeMap(): Record<string, string> {
    const map: Record<string, string> = {};
    this.categories.forEach(cat => {
      // course_categories 表的 code 字段作为 key，name 字段作为 value
      // 如果没有 code 字段，用 id 作为 key
      const key = (cat as any).code || cat.id;
      map[key] = cat.name;
    });
    return map;
  }

  /**
   * 根据 code 获取课程分类名称
   */
  getCategoryNameByCode(code: string): string {
    const cat = this.categories.find(c => (c as any).code === code);
    if (cat) return cat.name;
    // 尀试用 id 匹配
    const catById = this.categories.find(c => c.id === code);
    if (catById) return catById.name;
    return code;
  }

  /**
   * 根据 id 获取考评体系名称
   */
  getOrgNameById(id: string): string {
    const org = this.organizations.find(o => o.id === id);
    return org ? org.name : '';
  }

  /**
   * 根据 id 获取级别名称
   */
  getLevelNameById(id: string): string {
    const level = this.levels.find(l => l.id === id);
    return level ? level.name : '';
  }

  async addOrganization(org: Omit<Organization, 'id' | 'sortOrder'>): Promise<Organization> {
    const existingOrg = this.organizations.find(o => o.name === org.name.trim());
    if (existingOrg) {
      throw new Error('考评体系名称已存在，请使用其他名称');
    }

    const maxOrder = this.organizations.length > 0
      ? Math.max(...this.organizations.map(o => o.sortOrder))
      : 0;

    const { data, error } = await supabase
      .from('organizations')
      .insert({
        name: org.name,
        sort_order: maxOrder + 1,
        is_enabled: true
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('考评体系名称已存在，请使用其他名称');
      }
      throw error;
    }

    const newOrg: Organization = {
      id: data.id.toString(),
      name: data.name,
      sortOrder: data.sort_order
    };
    this.organizations.push(newOrg);
    this.notify();
    return newOrg;
  }

  async updateOrganization(id: string, updates: Partial<Organization>): Promise<void> {
    if (updates.name) {
      const existingOrg = this.organizations.find(o => o.name === updates.name.trim() && o.id !== id);
      if (existingOrg) {
        throw new Error('考评体系名称已存在，请使用其他名称');
      }
    }

    const { error } = await supabase
      .from('organizations')
      .update({
        name: updates.name,
        sort_order: updates.sortOrder
      })
      .eq('id', parseInt(id));

    if (error) {
      if (error.code === '23505') {
        throw new Error('考评体系名称已存在，请使用其他名称');
      }
      throw error;
    }

    const index = this.organizations.findIndex(o => o.id === id);
    if (index !== -1) {
      this.organizations[index] = { ...this.organizations[index], ...updates };
      this.notify();
    }
  }

  async deleteOrganization(id: string): Promise<void> {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', parseInt(id));

    if (error) throw error;

    this.organizations = this.organizations.filter(o => o.id !== id);
    this.categories = this.categories.filter(c => c.orgId !== id);
    this.levels = this.levels.filter(l => {
      const cat = this.categories.find(c => c.id === l.catId);
      return cat !== undefined;
    });
    this.notify();
  }

  async addCategory(cat: Omit<CourseCategory, 'id' | 'sortOrder'>): Promise<CourseCategory> {
    const maxOrder = this.categories.length > 0
      ? Math.max(...this.categories.map(c => c.sortOrder))
      : 0;

    const { data, error } = await supabase
      .from('course_categories')
      .insert({
        name: cat.name,
        org_id: parseInt(cat.orgId),
        sort_order: maxOrder + 1,
        is_enabled: true
      })
      .select()
      .single();

    if (error) throw error;

    const newCat: CourseCategory = {
      id: data.id.toString(),
      name: data.name,
      code: data.code || data.name.toLowerCase(),
      orgId: data.org_id?.toString() || '',
      sortOrder: data.sort_order
    };
    this.categories.push(newCat);
    this.notify();
    return newCat;
  }

  async updateCategory(id: string, updates: Partial<CourseCategory>): Promise<void> {
    const { error } = await supabase
      .from('course_categories')
      .update({
        name: updates.name,
        org_id: updates.orgId ? parseInt(updates.orgId) : undefined,
        sort_order: updates.sortOrder
      })
      .eq('id', parseInt(id));

    if (error) throw error;

    const index = this.categories.findIndex(c => c.id === id);
    if (index !== -1) {
      this.categories[index] = { ...this.categories[index], ...updates };
      this.notify();
    }
  }

  async deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('course_categories')
      .delete()
      .eq('id', parseInt(id));

    if (error) throw error;

    this.categories = this.categories.filter(c => c.id !== id);
    this.levels = this.levels.filter(l => l.catId !== id);
    this.notify();
  }

  async addLevel(level: Omit<Level, 'id' | 'sortOrder'>): Promise<Level> {
    const maxOrder = this.levels.length > 0
      ? Math.max(...this.levels.map(l => l.sortOrder))
      : 0;

    const { data, error } = await supabase
      .from('exam_levels')
      .insert({
        level_name: level.name,
        cat_id: parseInt(level.catId),
        sort_order: maxOrder + 1,
        is_enabled: true,
        price_month: level.priceMonth || 0,
        price_quarter: level.priceQuarter || 0,
        price_half_year: level.priceHalfYear || 0,
        price_year: level.priceYear || 0
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('该考评体系下的课程分类中，已存在同名级别，请修改后再提交');
      }
      throw error;
    }

    const newLevel: Level = {
      id: data.id.toString(),
      name: data.level_name,
      catId: data.cat_id?.toString() || '',
      sortOrder: data.sort_order,
      priceMonth: data.price_month,
      priceQuarter: data.price_quarter,
      priceHalfYear: data.price_half_year,
      priceYear: data.price_year,
      singleChoiceCount: data.single_choice_count || 0,
      multiChoiceCount: data.multi_choice_count || 0,
      judgeCount: data.judge_count || 0,
      programmingCount: data.programming_count || 0
    };
    this.levels.push(newLevel);

    await this.initDefaultInstitutionPrices(newLevel.id);

    this.notify();
    return newLevel;
  }

  private async initDefaultInstitutionPrices(levelId: string): Promise<void> {
    const defaultPeopleCounts = [10, 30, 50, 100];
    
    for (let i = 0; i < defaultPeopleCounts.length; i++) {
      try {
        await supabase
          .from('institution_prices')
          .insert({
            level_id: parseInt(levelId),
            people_count: defaultPeopleCounts[i],
            price_month: 0,
            price_quarter: 0,
            price_half_year: 0,
            price_year: 0,
            sort_order: i + 1,
            is_enabled: true
          });
      } catch (err) {
        console.error('初始化默认机构定价失败:', err);
      }
    }

    await this.loadInstitutionPrices();
  }

  async updateLevel(id: string, updates: Partial<Level>): Promise<void> {
    const { error } = await supabase
      .from('exam_levels')
      .update({
        level_name: updates.name,
        cat_id: updates.catId ? parseInt(updates.catId) : undefined,
        sort_order: updates.sortOrder,
        price_month: updates.priceMonth,
        price_quarter: updates.priceQuarter,
        price_half_year: updates.priceHalfYear,
        price_year: updates.priceYear
      })
      .eq('id', parseInt(id));

    if (error) {
      if (error.code === '23505') {
        throw new Error('该考评体系下的课程分类中，已存在同名级别，请修改后再提交');
      }
      throw error;
    }

    const index = this.levels.findIndex(l => l.id === id);
    if (index !== -1) {
      this.levels[index] = { ...this.levels[index], ...updates };
      this.notify();
    }
  }

  async updateLevelPrice(id: string, priceType: 'month' | 'quarter' | 'halfYear' | 'year', price: number): Promise<void> {
    if (price < 0) throw new Error('价格不能为负数');
    
    const updateData: Record<string, number> = {};
    switch (priceType) {
      case 'month': updateData.price_month = price; break;
      case 'quarter': updateData.price_quarter = price; break;
      case 'halfYear': updateData.price_half_year = price; break;
      case 'year': updateData.price_year = price; break;
    }

    const { error } = await supabase
      .from('exam_levels')
      .update(updateData)
      .eq('id', parseInt(id));

    if (error) throw error;

    const index = this.levels.findIndex(l => l.id === id);
    if (index !== -1) {
      switch (priceType) {
        case 'month': this.levels[index].priceMonth = price; break;
        case 'quarter': this.levels[index].priceQuarter = price; break;
        case 'halfYear': this.levels[index].priceHalfYear = price; break;
        case 'year': this.levels[index].priceYear = price; break;
      }
      this.notify();
    }
  }

  async batchUpdateLevelPrices(ids: string[], prices: { month?: number; quarter?: number; halfYear?: number; year?: number }): Promise<void> {
    const updateData: Record<string, number> = {};
    if (prices.month !== undefined) updateData.price_month = prices.month;
    if (prices.quarter !== undefined) updateData.price_quarter = prices.quarter;
    if (prices.halfYear !== undefined) updateData.price_half_year = prices.halfYear;
    if (prices.year !== undefined) updateData.price_year = prices.year;

    const { error } = await supabase
      .from('exam_levels')
      .update(updateData)
      .in('id', ids.map(id => parseInt(id)));

    if (error) throw error;

    ids.forEach(id => {
      const index = this.levels.findIndex(l => l.id === id);
      if (index !== -1) {
        if (prices.month !== undefined) this.levels[index].priceMonth = prices.month;
        if (prices.quarter !== undefined) this.levels[index].priceQuarter = prices.quarter;
        if (prices.halfYear !== undefined) this.levels[index].priceHalfYear = prices.halfYear;
        if (prices.year !== undefined) this.levels[index].priceYear = prices.year;
      }
    });
    this.notify();
  }

  async deleteLevel(id: string): Promise<void> {
    const { error } = await supabase
      .from('exam_levels')
      .delete()
      .eq('id', parseInt(id));

    if (error) throw error;

    this.levels = this.levels.filter(l => l.id !== id);
    this.institutionPrices = this.institutionPrices.filter(p => p.levelId !== id);
    this.notify();
  }

  async addInstitutionPrice(price: Omit<InstitutionPrice, 'id' | 'sortOrder'>): Promise<InstitutionPrice> {
    const existingPrices = this.institutionPrices.filter(p => p.levelId === price.levelId);
    const maxOrder = existingPrices.length > 0
      ? Math.max(...existingPrices.map(p => p.sortOrder))
      : 0;

    const { data, error } = await supabase
      .from('institution_prices')
      .insert({
        level_id: parseInt(price.levelId),
        people_count: price.peopleCount,
        price_month: price.priceMonth || 0,
        price_quarter: price.priceQuarter || 0,
        price_half_year: price.priceHalfYear || 0,
        price_year: price.priceYear || 0,
        sort_order: maxOrder + 1,
        is_enabled: true
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('该级别下已存在相同人数档位的定价');
      }
      throw error;
    }

    const newPrice: InstitutionPrice = {
      id: data.id.toString(),
      levelId: data.level_id.toString(),
      peopleCount: data.people_count,
      priceMonth: data.price_month,
      priceQuarter: data.price_quarter,
      priceHalfYear: data.price_half_year,
      priceYear: data.price_year,
      sortOrder: data.sort_order
    };
    this.institutionPrices.push(newPrice);
    this.notify();
    return newPrice;
  }

  async updateInstitutionPrice(id: string, updates: Partial<InstitutionPrice>): Promise<void> {
    const updateData: Record<string, any> = {};
    if (updates.peopleCount !== undefined) updateData.people_count = updates.peopleCount;
    if (updates.priceMonth !== undefined) updateData.price_month = updates.priceMonth;
    if (updates.priceQuarter !== undefined) updateData.price_quarter = updates.priceQuarter;
    if (updates.priceHalfYear !== undefined) updateData.price_half_year = updates.priceHalfYear;
    if (updates.priceYear !== undefined) updateData.price_year = updates.priceYear;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;

    const { error } = await supabase
      .from('institution_prices')
      .update(updateData)
      .eq('id', parseInt(id));

    if (error) {
      if (error.code === '23505') {
        throw new Error('该级别下已存在相同人数档位的定价');
      }
      throw error;
    }

    const index = this.institutionPrices.findIndex(p => p.id === id);
    if (index !== -1) {
      this.institutionPrices[index] = { ...this.institutionPrices[index], ...updates };
      this.notify();
    }
  }

  async deleteInstitutionPrice(id: string): Promise<void> {
    const { error } = await supabase
      .from('institution_prices')
      .delete()
      .eq('id', parseInt(id));

    if (error) throw error;

    this.institutionPrices = this.institutionPrices.filter(p => p.id !== id);
    this.notify();
  }

  async batchDeleteInstitutionPrices(ids: string[]): Promise<void> {
    const { error } = await supabase
      .from('institution_prices')
      .delete()
      .in('id', ids.map(id => parseInt(id)));

    if (error) throw error;

    this.institutionPrices = this.institutionPrices.filter(p => !ids.includes(p.id));
    this.notify();
  }

  async batchUpdateInstitutionPrices(ids: string[], prices: { month?: number; quarter?: number; halfYear?: number; year?: number }): Promise<void> {
    const updateData: Record<string, number> = {};
    if (prices.month !== undefined) updateData.price_month = prices.month;
    if (prices.quarter !== undefined) updateData.price_quarter = prices.quarter;
    if (prices.halfYear !== undefined) updateData.price_half_year = prices.halfYear;
    if (prices.year !== undefined) updateData.price_year = prices.year;

    const { error } = await supabase
      .from('institution_prices')
      .update(updateData)
      .in('id', ids.map(id => parseInt(id)));

    if (error) throw error;

    ids.forEach(id => {
      const index = this.institutionPrices.findIndex(p => p.id === id);
      if (index !== -1) {
        if (prices.month !== undefined) this.institutionPrices[index].priceMonth = prices.month;
        if (prices.quarter !== undefined) this.institutionPrices[index].priceQuarter = prices.quarter;
        if (prices.halfYear !== undefined) this.institutionPrices[index].priceHalfYear = prices.halfYear;
        if (prices.year !== undefined) this.institutionPrices[index].priceYear = prices.year;
      }
    });
    this.notify();
  }

  async moveInstitutionPrice(id: string, direction: 'up' | 'down'): Promise<void> {
    const levelId = this.institutionPrices.find(p => p.id === id)?.levelId;
    if (!levelId) return;

    const sorted = this.getInstitutionPricesByLevel(levelId);
    const index = sorted.findIndex(p => p.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sorted.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = sorted[index].sortOrder;
    sorted[index].sortOrder = sorted[targetIndex].sortOrder;
    sorted[targetIndex].sortOrder = temp;

    await this.updateInstitutionPrice(sorted[index].id, { sortOrder: sorted[index].sortOrder });
    await this.updateInstitutionPrice(sorted[targetIndex].id, { sortOrder: sorted[targetIndex].sortOrder });
  }

  async copyInstitutionPrices(sourceLevelId: string, targetLevelIds: string[], overwrite: boolean): Promise<number> {
    const sourcePrices = this.institutionPrices.filter(p => (p as any).levelId === sourceLevelId);
    if (sourcePrices.length === 0) return 0;

    let copiedCount = 0;

    for (const targetLevelId of targetLevelIds) {
      if (targetLevelId === sourceLevelId) continue;

      const existingPrices = this.institutionPrices.filter(p => (p as any).levelId === targetLevelId);

      if (overwrite && existingPrices.length > 0) {
        const idsToDelete = existingPrices.map(p => p.id);
        await this.batchDeleteInstitutionPrices(idsToDelete);
      }

      for (let i = 0; i < sourcePrices.length; i++) {
        const sourcePrice = sourcePrices[i];
        try {
          await supabase
            .from('institution_prices')
            .insert({
              level_id: parseInt(targetLevelId),
              people_count: sourcePrice.peopleCount,
              price_month: sourcePrice.priceMonth,
              price_quarter: sourcePrice.priceQuarter,
              price_half_year: sourcePrice.priceHalfYear,
              price_year: sourcePrice.priceYear,
              sort_order: sourcePrice.sortOrder,
              is_enabled: true
            });
          copiedCount++;
        } catch (err) {
          console.error('复制机构定价失败:', err);
        }
      }
    }

    await this.loadInstitutionPrices();
    return copiedCount;
  }

  async moveOrganization(id: string, direction: 'up' | 'down'): Promise<void> {
    const sorted = this.getOrganizations();
    const index = sorted.findIndex(o => o.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sorted.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = sorted[index].sortOrder;
    sorted[index].sortOrder = sorted[targetIndex].sortOrder;
    sorted[targetIndex].sortOrder = temp;

    await this.updateOrganization(sorted[index].id, { sortOrder: sorted[index].sortOrder });
    await this.updateOrganization(sorted[targetIndex].id, { sortOrder: sorted[targetIndex].sortOrder });
  }

  async moveCategory(id: string, direction: 'up' | 'down'): Promise<void> {
    const sorted = this.getCategories();
    const index = sorted.findIndex(c => c.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sorted.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = sorted[index].sortOrder;
    sorted[index].sortOrder = sorted[targetIndex].sortOrder;
    sorted[targetIndex].sortOrder = temp;

    await this.updateCategory(sorted[index].id, { sortOrder: sorted[index].sortOrder });
    await this.updateCategory(sorted[targetIndex].id, { sortOrder: sorted[targetIndex].sortOrder });
  }

  async moveLevel(id: string, direction: 'up' | 'down'): Promise<void> {
    const sorted = this.getLevels();
    const index = sorted.findIndex(l => l.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sorted.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = sorted[index].sortOrder;
    sorted[index].sortOrder = sorted[targetIndex].sortOrder;
    sorted[targetIndex].sortOrder = temp;

    await this.updateLevel(sorted[index].id, { sortOrder: sorted[index].sortOrder });
    await this.updateLevel(sorted[targetIndex].id, { sortOrder: sorted[targetIndex].sortOrder });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(l => {
      try {
        l();
      } catch (err) {
        console.error('通知监听器失败:', err);
      }
    });
  }
}

export const categoryStore = new CategoryStore();
